<template>
  <v-app>
    <div>
      <v-toolbar elevation="0">
        <router-link class="text-decoration-none" to="/settings?tab=user-management">
          <v-btn icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </router-link>
        <v-toolbar-title>{{$t('UserDetails.AccessLevel')}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn class="ml-5 red white--text" text @click="deleteUser" :disabled="isFacilityUser || isFacilityAdmin">{{$t('UserDetails.DeleteUser')}}</v-btn>
      </v-toolbar>
      <v-divider></v-divider>

      <v-container v-if="user" fluid grid-list-lg>
        <v-layout wrap>
          <v-flex lg4 md4 xs12>
            <v-card :min-height="flexHeight" elevation="12">
              <v-card-title class="pb-5">
                {{$t('GeneralTranslations.Profile')}}
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-img
                    id="avatar"
                    ref="avatar"
                    :src="user.profilePicUrl"
                    height="280"
                    min-height="250"
                  >
                  </v-img>
                </v-row>

                <v-row class="pt-3 px-2">
                  <v-btn icon @click="openGalleryPhotos()">
                    <v-icon class="ignore-readonly" color="#3F51B5">mdi-image-multiple</v-icon>
                  </v-btn>

                  <v-btn
                      icon
                      @click="$refs.inputUpload.click()"
                  >
                    <v-icon color="#3F51B5">mdi-camera</v-icon>
                  </v-btn>

                  <input
                      v-show="false"
                      ref="inputUpload"
                      type="file"
                      @change="saveImage"
                  />

                  <v-spacer></v-spacer>

                  <div>
                    <v-btn
                      v-if="currentUser.accessLevel === accessLevelsEnum.OrgAdmin"
                      @click="toggleEditing"
                      color="primary"
                      outlined
                    >
                      {{
                        editing ? $t('GeneralTranslations.Cancel') : $t('GeneralTranslations.Edit')
                      }}
                    </v-btn>

                    <v-btn
                      v-if="editing"
                      @click="updateUserAccessLevel"
                      color="primary"
                      class="ml-3"
                      :loading="loading"
                    >
                      {{$t('GeneralTranslations.Save')}}</v-btn>
                  </div>
                </v-row>

                <v-row>
                  <v-col cols="4">
                    <b>{{$t('VeeValidate.Fields.Name')}}</b>
                  </v-col>
                  <v-col cols="6">
                    {{ user.name }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="4">
                    <b>{{$t('VeeValidate.Fields.Username')}}</b>
                  </v-col>
                  <v-col cols="6">
                    {{ user.email }}
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="4">
                    <b>{{$t('UserDetails.AccessLevel')}}</b>
                  </v-col>
                  <v-col cols="6" v-if="editing">
                    <v-select :disabled="currentUser.accessLevel !== accessLevelsEnum.OrgAdmin" :items="accessLevels"
                      v-model="accessLevel">
                    </v-select>
                  </v-col>
                  <v-col cols="6" v-else>
                    {{ user.accessLevel }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="4">
                    <b> {{$t('GeneralTranslations.Role')}}</b>
                  </v-col>
                  <v-col cols="6" v-if="editing">
                    <v-select
                      v-model="user.roles"
                      :items="usersRoles"
                      item-text="name"
                      :label="$t('GeneralTranslations.Role')"
                      multiple
                    ></v-select>
                  </v-col>
                  <v-col cols="6" v-else>
                    {{ user.roles ? user.roles.join(", ") : ""}}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex lg8 md8 xs12>
            <v-card :min-height="flexHeight" elevation="12">
              <v-card-title>{{$t('UserDetails.FacilityAccess')}}</v-card-title>
              <v-container>
                <v-card-text>
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">{{$t('GeneralTranslations.Facility')}}</th>
                          <th class="text-left"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in user.facilities" :key="item.name">
                          <td>{{ item.name }}</td>
                          <td class="text-right">
                            <v-btn
                                class="ml-5 red white--text"
                                text
                                @click="removeFacilityFromUser(item)"
                                :disabled="isFacilityUser || isFacilityAdmin"
                            >
                              {{$t('GeneralTranslations.Remove')}}
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-spacer></v-spacer>
                </v-card-text>
                <v-card-actions class="pt-4">
                  <v-spacer></v-spacer>
                  <v-dialog v-model="dialog" max-width="600px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-if="filteredStates.length > 0" class="ml-5" color="primary" v-bind="attrs" v-on="on" :disabled="isFacilityUser || isFacilityAdmin">{{$t('UserDetails.AddFacility')}}</v-btn>
                      <v-btn class="ml-5" color="primary" @click="resetPassword" :disabled="isFacilityUser || isFacilityAdmin">
                        {{$t('UserDetails.ResetPassword')}}
                      </v-btn>
                    </template>
                    <v-card class="pb-4 px-4">
                      <v-card-title>
                        <span class="headline">{{$t('UserDetails.PickFacility')}}</span>
                      </v-card-title>
                      <v-card-text>
                      <v-select v-model="facilityToAdd" :items="filteredStates" color="primary"
                          :rules="[v => !!v || 'You must select a Facility']" class="px-0 mx-0 pt-3" item-text="name"
                          :label="$t('GeneralTranslations.Facility')" required return-object></v-select>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn color="primary" @click="addFacilityToUser">{{$t('UserDetails.AddFacility')}}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-card-actions>
              </v-container>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
    <ConfirmDlg ref="confirmDelete" />

    <ConfirmMessageDlg ref="confirmResetPassword" />

    <GalleryPhotosUploadDlg
      ref="GalleryPhotosUploadDlg"
      @uploadGalleryPhoto="uploadGalleryPhoto"
    />
  </v-app>
</template>

<style scoped>
.v-list {
  height: 300px;
  overflow-y: auto;
}

.datepickers {
  padding: 0px !important;
}
.v-btn.v-btn--disabled {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
</style>
<script>
import { mapState } from "vuex";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import ConfirmMessageDlg from "@/components/popup/confirm-message-dialog/ConfirmMessageDlg.vue";
import { StaffAccessLevels } from "@/enums/StaffAccessLevels";
import firebase from "firebase";
import { getRegionName } from "@/utils/functionsRegion";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {v4 as uuidv4} from "uuid";
import { optimizePhotos } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import {waitResizedImage} from "@/utils/wait-resized-image";

export default {
  name: "UserDetails",
  components: {
    ConfirmDlg,
    ConfirmMessageDlg,
    GalleryPhotosUploadDlg
  },
  data: () => ({
    flexHeight: 410,
    facilityToAdd: null,
    dialog: false,
    editing: false,
    accessLevels: Object.values(StaffAccessLevels),
    accessLevelsEnum: StaffAccessLevels,
    accessLevel: "",
    loading: false,
    isChangedRole: false,
  }),

  computed: {
    ...mapState({
      user: state => state.userModule.selecteduser,
      facilitiesForOrg: state => state.userModule.facilitiesForOrg,
      currentUser: state => state.authModule.user
    }),
    usersRoles() {
      return this.$store.state.userModule.usersRoles;
    },
    isFacilityUser() {
      return this.currentUser.accessLevel === this.accessLevelsEnum.FacilityUser;
    },
    isFacilityAdmin() {
      return this.currentUser.accessLevel === this.accessLevelsEnum.FacilityAdmin;
    },
    currentFacility() {
      return this.$store.state.authModule.currentFacility;
    },
    filteredStates() {
      return this.$store.state.userModule.facilitiesForOrg?.filter(facility => !this.$store.state.userModule.selecteduser?.facilitiesIds?.includes(facility.id));
    },
  },
  created() {
    this.$store.dispatch("getSelectedUser", this.$route.params.id);
    this.$store.dispatch("getFacilitiesForOrg");
    this.$store.dispatch('getUsersRoles');
    this.$store.dispatch("getMaintenanceCategories");
  },
  watch: {
    user(user) {
      if (user) {
        this.accessLevel = this.user.accessLevel;
      }
    },
    "user.roles"() {
      this.isChangedRole = true;
    }
  },
  methods: {
    removeFacilityFromUser(facility) {
      const facilities = this.user.facilities.filter(function (el) {
        return el.id != facility.id;
      });
      const facilitiesIds = this.user.facilitiesIds.filter(function (el) {
        return el != facility.id;
      });

      this.user.facilities = facilities;
      this.user.facilitiesIds = facilitiesIds;

      if (this.user.facilityId === facility.id && this.user.facilitiesIds[0]) {
        this.user.facilityId = this.user.facilitiesIds[0]
        console.log('new Facility id: ' + this.user.facilityId)
        this.$store.dispatch("changeUserFacility", { userId: this.user.id, facilityId: this.user.facilityId }).then((result) => {
          if (result && result.data && result.data.status === 200) {
            this.$store.dispatch("updateUser", this.user);
          }
          else {
            console.error('Unable to update user facility')
          }
        })
      }
      else {
        this.$store.dispatch("updateUser", this.user);
      }
    },
    addFacilityToUser() {
      const facilityIdToAdd = this.facilityToAdd.id.toString()
      this.user.facilities.push({
        id: facilityIdToAdd,
        name: this.facilityToAdd.name
      });
      this.user.facilitiesIds.push(facilityIdToAdd);
      this.$store.dispatch("updateUser", this.user);
      this.facilityToAdd = null;
      this.dialog = false;
    },

    deleteUser() {
      try {
        this.$refs.confirmDelete.open(
          this.$i18n.messages[this.$i18n.locale]['UserDetails.DeleteUser'],
          this.$i18n.messages[this.$i18n.locale]['UserDetails.DeleteUserMessage'],
          false,
          this.$i18n.messages[this.$i18n.locale]['UserDetails.ReasonSharedWithVisitor']
        )
          .then(response => {
            if (response && response.agree) {
              this.$store.dispatch("deleteUser", this.user.id).then(() => {
                this.$router.push("/settings?tab=user-management");
              });
            }
          });

      } catch (error) {
        console.error(error);
      }
    },

    toggleEditing() {
      this.editing = !this.editing;
    },

    async updateUserAccessLevel() {
      this.loading = true;
      if(!this.isChangedRole) {
        await this.$store.dispatch("updateUserAccessLevel", {
          ...this.user,
          accessLevel: this.accessLevel
        });
      } else {
        const rolesIds = this.user.roles.map(roleName => {
          const role = this.usersRoles.find(x => x.name === roleName);
          if(role) {
            return role.id;
          }
        });

        await this.$store.dispatch("updateUserAccessLevelAndRole", {
          ...this.user,
          accessLevel: this.accessLevel,
          rolesIds: rolesIds
        });
      }

      this.editing = false;
      this.loading = false;
      this.isChangedRole = false;
    },

    resetPassword() {
      (this.$refs.confirmResetPassword)
        .open(null, this.$i18n.messages[this.$i18n.locale]['UserDetails.ResetPasswordEmailQuestion'], false, "", null, 
        this.$i18n.messages[this.$i18n.locale]['UserDetails.PasswordHasBeenResetMessage'])
        .then(response => {
          if (response && response.agree) {
            firebase.app().functions(getRegionName()).httpsCallable("resetUserPassword")({
              userId: this.$route.params.id
            })
          }
        });
    },

    openGalleryPhotos() {
      this.$refs.GalleryPhotosUploadDlg.open();
    },

    uploadGalleryPhoto(image) {
      this.user.profilePicUrl = image.image;
      this.user.thumbnailUrl = image?.thumbnailUrl ?? image.image;

      this.$store.dispatch("updateUserPhoto", {
        ...this.user,
        profilePicUrl: image.image,
        thumbnailUrl: image?.thumbnailUrl ?? image.image
      });
    },

    async saveImage(event) {
      const ref = uuidv4();
      const file = await optimizePhotos(event.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        return;
      }

      const storageRef = firebase
        .storage()
        .ref()
        .child(this.currentFacility)
        .child("staff")
        .child(this.user.id.toString())
        .child("profilePic")
        .child(ref)
        .put(file);

      const path = (await storageRef)?.metadata?.fullPath
      const thumbnailImageRef = firebase.storage().ref().child(`${path}_200x200`);

      storageRef.on(
          `state_changed`,
          snapshot => {
            console.log("Uploading...")
          },
          error => {
            console.error(error.message);
          },
          () => {
            storageRef.snapshot.ref.getDownloadURL().then(async url => {
              const thumbnailUrl = await waitResizedImage(10, thumbnailImageRef);
              firebase
                  .firestore()
                  .collection("users")
                  .doc(this.user.id)
                  .update({
                    profilePicUrl: url,
                    thumbnailUrl: thumbnailUrl
                  })
                  .then(async () => {
                    this.user.profilePicUrl = url;
                    await this.$store.dispatch("getUser");
                  });
            });
          }
      );

    }
  }
};
</script>
